import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Text, Button, Checkbox, Spinner, Badge } from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  QrCodeRegular,
  ArrowDownloadRegular,
  PrintRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  API_URL,
} from '../../../configurations/index.config';
import type { Checkpoint, Site } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  checkpointGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '16px',
  },
  checkpointCard: {
    padding: '16px',
    border: '1px solid #edebe9',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#f3f2f1',
    },
  },
  selectedCard: {
    border: '2px solid #0078d4',
    backgroundColor: '#eff6fc',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #edebe9',
  },
  qrPreviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '24px',
  },
  qrPreviewCard: {
    padding: '16px',
    border: '1px solid #edebe9',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  qrImage: {
    width: '150px',
    height: '150px',
    border: '1px solid #edebe9',
  },
});

const QRBatchGenerationFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<Set<number>>(new Set());
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(undefined);
  const [generatedQRs, setGeneratedQRs] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, [selectedSiteId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedSiteId) {
        params.site_id = selectedSiteId;
      }
      params.status = 'active';

      const [checkpointsRes, sitesRes] = await Promise.all([
        ApiClient.get('/get-checkpoints', { params }),
        ApiClient.get('/get-sites'),
      ]);

      if (checkpointsRes.data.status === 'success') {
        setCheckpoints(checkpointsRes.data.data || []);
      }

      if (sitesRes.data.status === 'success') {
        setSites(sitesRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleSelectCheckpoint = (id: number) => {
    const newSelected = new Set(selectedCheckpoints);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCheckpoints(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCheckpoints.size === checkpoints.length) {
      setSelectedCheckpoints(new Set());
    } else {
      setSelectedCheckpoints(new Set(checkpoints.map(c => c.id)));
    }
  };

  const handleGenerateQRCodes = async () => {
    if (selectedCheckpoints.size === 0) {
      newToast('error', 'Please select at least one checkpoint');
      return;
    }

    setGenerating(true);
    try {
      const response = await ApiClient.post('/generate-qr-batch', {
        checkpoint_ids: Array.from(selectedCheckpoints),
      });

      if (response.data.status === 'success') {
        newToast('success', `QR codes generated for ${selectedCheckpoints.size} checkpoints`);
        setGeneratedQRs(response.data.data.qr_images);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setGenerating(false);
  };

  const handleDownloadAll = async () => {
    for (const qr of generatedQRs) {
      const imageUrl = `${API_URL}${qr.qr_image_url}`;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR-${qr.checkpoint_name}.png`;
      a.click();
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    newToast('success', 'All QR codes downloaded');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading checkpoints..." />
      </div>
    );
  }

  return (
    <PermissionGate privilege="qr.generate" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <Button
              appearance="subtle"
              icon={<ArrowLeftRegular />}
              onClick={() => navigate('/account/checkpoints')}
            />
            <Text className={styles.pageTitle}>QR Code Batch Generation</Text>
          </div>
        </div>

        <ContentCard>
          <Text size={400} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
            Select Checkpoints
          </Text>

          <div className={styles.filters}>
            <SearchableDropdown
              placeholder="Filter by Site"
              selectedValue={selectedSiteId ? selectedSiteId.toString() : 'all'}
              onSelect={value => {
                setSelectedSiteId(value === 'all' ? undefined : Number(value));
              }}
              options={[
                { id: 'all', label: 'All Sites' },
                ...sites.map(site => ({
                  id: site.id.toString(),
                  label: site.name,
                })),
              ]}
            />

            <Button appearance="secondary" onClick={handleSelectAll}>
              {selectedCheckpoints.size === checkpoints.length ? 'Deselect All' : 'Select All'}
            </Button>

            <Text style={{ marginLeft: 'auto', color: '#605e5c' }}>
              {selectedCheckpoints.size} of {checkpoints.length} selected
            </Text>
          </div>

          <div className={styles.checkpointGrid}>
            {checkpoints.map(checkpoint => (
              <div
                key={checkpoint.id}
                className={`${styles.checkpointCard} ${
                  selectedCheckpoints.has(checkpoint.id) ? styles.selectedCard : ''
                }`}
                onClick={() => handleSelectCheckpoint(checkpoint.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox
                    checked={selectedCheckpoints.has(checkpoint.id)}
                    onChange={() => handleSelectCheckpoint(checkpoint.id)}
                  />
                  <Text weight="semibold">{checkpoint.checkpoint_name}</Text>
                </div>
                <Text size={200} style={{ color: '#605e5c' }}>
                  {checkpoint.site_name}
                </Text>
                <Badge appearance="tint" color="brand" size="small">
                  {checkpoint.status}
                </Badge>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={<QrCodeRegular />}
              onClick={handleGenerateQRCodes}
              disabled={generating || selectedCheckpoints.size === 0}
            >
              {generating ? 'Generating...' : `Generate QR Codes (${selectedCheckpoints.size})`}
            </Button>
          </div>
        </ContentCard>

        {generatedQRs.length > 0 && (
          <ContentCard>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Text size={400} weight="semibold">
                Generated QR Codes ({generatedQRs.length})
              </Text>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  appearance="secondary"
                  icon={<ArrowDownloadRegular />}
                  onClick={handleDownloadAll}
                >
                  Download All
                </Button>
                <Button appearance="secondary" icon={<PrintRegular />} onClick={handlePrint}>
                  Print
                </Button>
              </div>
            </div>

            <div className={styles.qrPreviewGrid}>
              {generatedQRs.map(qr => (
                <div key={qr.checkpoint_id} className={styles.qrPreviewCard}>
                  <img
                    src={`${API_URL}${qr.qr_image_url}`}
                    alt={qr.checkpoint_name}
                    className={styles.qrImage}
                  />
                  <Text weight="semibold" size={300} style={{ textAlign: 'center' }}>
                    {qr.checkpoint_name}
                  </Text>
                  <Text
                    size={200}
                    style={{ color: '#605e5c', fontFamily: 'monospace', fontSize: '10px' }}
                  >
                    {qr.qr_token.substring(0, 16)}...
                  </Text>
                </div>
              ))}
            </div>
          </ContentCard>
        )}
      </div>
    </PermissionGate>
  );
};

export default QRBatchGenerationFluentUI;
